import ModalContainer from '@/components/molecules/ModalContainer';
import { TUserContactInfo, TUserInviteInfo } from '@/types/User.types';
import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EMAIL_REGEX, NAME_REGEX } from '@/constants/stringVars';
import * as yup from 'yup';
import { Center, Checkbox, HStack, Image, Input, Select, Text, VStack } from '@chakra-ui/react';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import iconsPng from '@/assets/img/png/icons';
import CustomButton from '@/components/atoms/CustomButton';
import useAddMemberToOrganization from '@/hooks/organization/addMemberToOrganization';
import { useAuthContext } from '@/context/auth/useAuthContext';
import colors from '@/theme/colors';
import { USER_AFFILIATION_OPTIONS } from '@/constants';
import { capitalize } from '@/utils/stringUtils';
import { TRosterUserPeerSupportCertificateInfo } from '@/types/Roster.types';
import PeerSupportCertificationForm from './PeerSupportCertificationForm';
import InviteFailures from '@/components/organization/InviteFailures';
import { getInviteFailures } from '@/utils/inviteFailures';

const AFFILIATION_DEFAULT_VALUE = 'Affiliation';

interface Props {
  addMemberModalVisible: boolean;
  setAddMemberModalVisible: Dispatch<SetStateAction<boolean>>;
  reFetch: () => void;
}

const addMemberSchema = yup
  .object()
  .shape({
    firstName: yup
      .string()
      .required('First Name is required.')
      .matches(NAME_REGEX, 'Only alphabetic characters allowed'),
    lastName: yup.string().required('Last Name is required.').matches(NAME_REGEX, 'Only alphabetic characters allowed'),
    phone: yup.string().required('Phone is required.'),
    email: yup.string().matches(EMAIL_REGEX, 'Wrong email format').required('Email is required.'),
    userAffiliation: yup
      .string()
      .required('Affiliation is required')
      .notOneOf([AFFILIATION_DEFAULT_VALUE], 'Affiliation is required'),
    isPeerSupport: yup.boolean(),
    dateOfIssue: yup.string().when('isPeerSupport', {
      is: true,
      then: (schema) => schema.required('Date of Issue is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
    validUntil: yup.string().when('isPeerSupport', {
      is: true,
      then: (schema) => schema.required('Valid Until is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
    certificateId: yup.string().when('isPeerSupport', {
      is: true,
      then: (schema) => schema.required('Certificate ID is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })
  .required();

const OrganizationRosterAddMemberModal = ({ addMemberModalVisible, setAddMemberModalVisible, reFetch }: Props) => {
  const { isSuperAdmin } = useAuthContext();

  const methods = useForm<
    TUserContactInfo & {
      userAffiliation: string;
      isPeerSupport?: boolean;
    } & Partial<TRosterUserPeerSupportCertificateInfo>
  >({
    resolver: yupResolver(addMemberSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
    },
  });
  const { register, getValues, reset: resetUserForm, formState, watch, setValue } = methods;

  const { isValid, errors, touchedFields } = formState;

  const affiliationValue = watch('userAffiliation');
  const isAffiliationTouched = touchedFields.userAffiliation;

  const isPeerSupportValue = watch('isPeerSupport');

  const {
    isPending,
    mutate: inviteMembersToOrg,
    data,
    error,
  } = useAddMemberToOrganization(() => {
    reFetch();
    resetUserForm();
    setValue('userAffiliation', AFFILIATION_DEFAULT_VALUE);
    setValue('isPeerSupport', false);
    setAddMemberModalVisible(false);
  });
  const inviteFailures = data?.failed ?? getInviteFailures(error);

  const addMember = useCallback(async () => {
    const userToInvite = getValues();

    userToInvite.userAffiliation = userToInvite.userAffiliation.toUpperCase();

    const userToInviteBE: TUserInviteInfo = {
      ...userToInvite,
      validationStartDate: userToInvite.dateOfIssue,
      validationEndDate: userToInvite.validUntil,
    };

    inviteMembersToOrg([userToInviteBE]);
  }, [getValues, inviteMembersToOrg]);

  return (
    <ModalContainer
      isOpen={addMemberModalVisible}
      onClose={() => setAddMemberModalVisible(false)}
      isBlur={false}
      width={'40%'}
      height={'20vh'}
      backgroundColor={'background.lightBlue'}
    >
      <Text variant={'loraTitle'} textAlign={'center'} fontSize={'26px'} marginTop={'30px'}>
        Add Member to your Organization
      </Text>
      <Center marginTop={'20px'}>
        <Image alt="add-member" src={iconsPng.addMember} width={120} height={120} />
      </Center>
      <Center>
        <InviteFailures failed={inviteFailures} />
        <VStack marginY={'40px'} width={FORM_FIELD_WIDTH} gap={5}>
          <Input
            {...register('firstName', {
              required: true,
            })}
            className="custom-input"
            placeholder={'First Name'}
            _placeholder={{
              fontWeight: '600',
              color: 'background.blueGray',
            }}
            data-state={touchedFields.firstName ? (errors.firstName?.message ? 'invalid' : 'valid') : ''}
          />
          {errors.firstName && <Text variant={'error'}>{errors.firstName.message}</Text>}
          <Input
            {...register('lastName', {
              required: true,
            })}
            className="custom-input"
            placeholder={'Last Name'}
            _placeholder={{
              fontWeight: '600',
              color: 'background.blueGray',
            }}
            data-state={touchedFields.lastName ? (errors.lastName?.message ? 'invalid' : 'valid') : ''}
          />
          {errors.lastName && <Text variant={'error'}>{errors.lastName.message}</Text>}
          <Input
            {...register('phone', {
              required: true,
            })}
            className="custom-input"
            placeholder={'Phone Number'}
            _placeholder={{
              fontWeight: '600',
              color: 'background.blueGray',
            }}
            data-state={touchedFields.phone ? (errors.phone?.message ? 'invalid' : 'valid') : ''}
          />
          {errors.phone && <Text variant={'error'}>{errors.phone.message}</Text>}

          <Input
            {...register('email', { required: true })}
            className="custom-input"
            placeholder={'Email Address'}
            type="email"
            _placeholder={{
              fontWeight: '600',
              color: 'background.blueGray',
            }}
            data-state={touchedFields.email ? (errors.email?.message ? 'invalid' : 'valid') : ''}
          />
          {errors.email && <Text variant={'error'}>{errors.email.message}</Text>}

          <Select
            className="custom-input custom-select-padding"
            focusBorderColor="transparent"
            defaultValue={AFFILIATION_DEFAULT_VALUE}
            isInvalid={!!errors?.userAffiliation}
            bg={colors.background.lightBlue}
            _invalid={{ border: `1px solid ${colors.extra.red}!` }}
            _valid={{ border: `1px solid ${colors.text.mediumBlue}` }}
            style={{
              fontSize: '14px',
              borderRadius: '8px',
              color: affiliationValue !== AFFILIATION_DEFAULT_VALUE ? colors.extra.black : colors.text.mediumGray,
            }}
            {...register(`userAffiliation`, {
              required: { message: 'Affiliation is required', value: true },
              validate: (value: string) => (value === '' ? 'Affiliation is required' : true),
            })}
            // this helps render border color as valid after selecting a value
            data-state={
              isAffiliationTouched ? (affiliationValue === AFFILIATION_DEFAULT_VALUE ? 'invalid' : 'valid') : ''
            }
          >
            <option disabled>{AFFILIATION_DEFAULT_VALUE}</option>
            {USER_AFFILIATION_OPTIONS.map((affiliationOption) => (
              <option key={affiliationOption} value={affiliationOption}>
                {capitalize(affiliationOption)}
              </option>
            ))}
          </Select>
          {errors.userAffiliation && <Text variant={'error'}>{errors.userAffiliation.message}</Text>}

          <HStack width={'100%'}>
            <Checkbox
              {...register('isPeerSupport')}
              colorScheme="primary"
              borderRadius={'4px'}
              size={'lg'}
              justifyContent={'center'}
              alignItems={'center'}
              className="customCheckbox"
            >
              <Text variant={'urbanistMedium'} color={isPeerSupportValue ? 'text.mediumBlue' : 'text.mediumGray'}>
                Add this member to Peer Support Team
              </Text>
            </Checkbox>
          </HStack>

          {isPeerSupportValue && (
            <VStack>
              <VStack marginY={'10px'} width={FORM_FIELD_WIDTH} gap={'20px'}>
                <PeerSupportCertificationForm methods={methods} />
              </VStack>
            </VStack>
          )}

          <CustomButton
            style={{ marginTop: '20px' }}
            backgroundColor="secondary.500"
            label="Invite Member"
            width={FORM_FIELD_WIDTH}
            labelColor="white"
            onClick={addMember}
            disabled={!isValid || isSuperAdmin}
            isLoading={isPending}
          />
        </VStack>
      </Center>
    </ModalContainer>
  );
};

export default OrganizationRosterAddMemberModal;
